/**
 * CRE Web App permission helpers.
 * Source of truth: user.permissions from TokenValidation/Validate.
 * isAdmin === true unlocks all permissions.
 */

export const CRE_PERMISSION = {
  BASIC: "BASIC",
  PROCESS_AI: "PROCESS_AI",
  VIDEO_RECORDING: "VIDEO_RECORDING",
  IMAGE_AI: "IMAGE_AI",
  MDT: "MDT",
  CHAT: "CHAT",
  SHARE_FILE: "SHARE_FILE",
  ACR: "ACR",
  PROTOCOLS: "PROTOCOLS",
  WINDOWS_INJECTION: "WINDOWS_INJECTION",
  BROWSER_INJECTION: "BROWSER_INJECTION",
  DOCUMENT_ANONYMIZATION: "DOCUMENT_ANONYMIZATION",
  SNIPPING_TOOL: "SNIPPING_TOOL",
};

/** Hospital/enterprise message (Jacky — commercial variant deferred). */
export const CRE_PERMISSION_DENIED_MESSAGE =
  "This module is not currently enabled for your user or organization. Please contact your organization administrator to enable access.";

/**
 * @param {Record<string, unknown> | null | undefined} creUser
 * @param {string} code
 * @returns {boolean}
 */
export function hasCrePermission(creUser, code) {
  if (!code || typeof code !== "string") return false;
  if (!creUser) return false;
  if (creUser.isAdmin === true) return true;
  const permissions = creUser.permissions;
  if (!Array.isArray(permissions)) return false;
  return permissions.includes(code);
}

/**
 * Normalize API / envelope errors that indicate a missing feature permission.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isCreFeatureNotAllowedError(error) {
  if (!error || typeof error !== "object") return false;
  const err = /** @type {Record<string, unknown>} */ (error);
  if (err.errorCode === "FEATURE_NOT_ALLOWED" || err.code === "FEATURE_NOT_ALLOWED") {
    return true;
  }
  const nested = err.errorInfo;
  if (nested && typeof nested === "object") {
    const info = /** @type {Record<string, unknown>} */ (nested);
    if (info.code === "FEATURE_NOT_ALLOWED" || info.errorCode === "FEATURE_NOT_ALLOWED") {
      return true;
    }
  }
  const data = err.data;
  if (data && typeof data === "object") {
    const body = /** @type {Record<string, unknown>} */ (data);
    if (body.errorCode === "FEATURE_NOT_ALLOWED") return true;
    const info = body.errorInfo;
    if (info && typeof info === "object") {
      const ei = /** @type {Record<string, unknown>} */ (info);
      if (ei.code === "FEATURE_NOT_ALLOWED") return true;
    }
  }
  return false;
}

/**
 * Prefer backend message when FEATURE_NOT_ALLOWED; otherwise hospital copy.
 * @param {unknown} [error]
 * @returns {string}
 */
export function getCrePermissionDeniedMessage(error) {
  if (error && typeof error === "object") {
    const err = /** @type {Record<string, unknown>} */ (error);
    if (typeof err.message === "string" && err.message.trim() && isCreFeatureNotAllowedError(error)) {
      return err.message.trim();
    }
    const nested = err.errorInfo;
    if (nested && typeof nested === "object") {
      const info = /** @type {Record<string, unknown>} */ (nested);
      if (typeof info.message === "string" && info.message.trim()) {
        return info.message.trim();
      }
    }
    const data = err.data;
    if (data && typeof data === "object") {
      const body = /** @type {Record<string, unknown>} */ (data);
      if (typeof body.message === "string" && body.message.trim()) {
        return body.message.trim();
      }
      const info = body.errorInfo;
      if (info && typeof info === "object") {
        const ei = /** @type {Record<string, unknown>} */ (info);
        if (typeof ei.message === "string" && ei.message.trim()) {
          return ei.message.trim();
        }
      }
    }
  }
  return CRE_PERMISSION_DENIED_MESSAGE;
}

/**
 * Catch-block helper: hospital/backend message when the error is FEATURE_NOT_ALLOWED,
 * otherwise null so the caller can fall back to its own toast / state handling.
 * @param {unknown} error
 * @returns {string | null}
 */
export function resolveCreFeatureNotAllowedMessage(error) {
  if (!isCreFeatureNotAllowedError(error)) return null;
  return getCrePermissionDeniedMessage(error);
}
